import { useGiveawayStats, useGiveawayUtils } from '@src/hooks/useGiveaways';
import { Link } from '@tanstack/react-router';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { RaffleV2, RaffleV2Type } from 'jigsaw';
import { HStack, Text, VStack } from 'ui_2';
import { Route as index_route } from '@routes/index';
import { Route as giveaway_ad_route } from '@routes/giveaways/$programId/ad';
import { useMemo } from 'react';
import eth_logo from '@assets/eth_logo.png';
import { GiveawayTimeRemaining } from './GiveawayTimeRemaining';
import { useViewTransition } from '@src/hooks/useViewTransition';
import { useArcadeStoreState } from '@src/hooks/useArcadeStoreState';
import { router } from '@src/providers/router';
import { getDeeplinkBrowserUrl } from '@puzzlehq/frontend-utils';

dayjs.extend(LocalizedFormat);

export const GiveawayHistoryRowView = ({
  giveaway,
}: {
  giveaway: RaffleV2;
}) => {
  const { isGiveawayOver, isGiveawayWinner } = useGiveawayUtils(giveaway);
  const { userGiveawayEntriesQuery } = useGiveawayStats(giveaway);

  const img = useMemo(() => {
    if (
      giveaway.type === RaffleV2Type.product ||
      giveaway.type === RaffleV2Type.eth
    ) {
      const smallImage =
        giveaway.productSku.smallImageURL ??
        giveaway.productSku.service.smallLogoImageURL;
      if (smallImage) return smallImage;
      if (giveaway.type === RaffleV2Type.eth) return eth_logo;
    }
    return giveaway?.sponsor.logoURL;
  }, [JSON.stringify(giveaway)]);

  const userEntries = userGiveawayEntriesQuery.data;

  const Pill = useMemo(() => {
    if (isGiveawayWinner) {
      return (
        <div className='rounded-full border border-purple-200 bg-purple-600 px-3 shadow-sm'>
          <Text color='white'>Won</Text>
        </div>
      );
    } else if (isGiveawayOver) {
      return (
        <div className='rounded-full bg-gray-200 px-3'>
          <Text>Lost</Text>
        </div>
      );
    } else if ((userEntries ?? 0) > 0) {
      return (
        <div className='rounded-full border border-purple-200 bg-purple-100 px-2 shadow-sm'>
          <Text>{userEntries} Entries</Text>
        </div>
      );
    }
  }, [JSON.stringify(giveaway), userEntries]);

  return (
    <HStack
      className={`w-full items-center gap-2 rounded-lg border border-gray-100 p-3 ${
        isGiveawayWinner ? 'bg-purple-100' : ''
      }`}
    >
      <img src={img} width={50} height={50} className='rounded-lg' />
      <VStack className='gap-0.5'>
        {!isGiveawayOver && <GiveawayTimeRemaining giveaway={giveaway} />}
        <Text font='sm'>{giveaway.title}</Text>
        <Text font='sm'>{giveaway.prizeDescription}</Text>
        {isGiveawayOver && giveaway.draw && (
          <Text font={'xs'}>Ended on {dayjs(giveaway.draw).format('L')}</Text>
        )}
      </VStack>
      <HStack className='flex-grow' />
      {Pill}
    </HStack>
  );
};

export const GiveawayHistoryRow = ({ giveaway }: { giveaway: RaffleV2 }) => {
  const { isPuzzleWalletWebview } = useArcadeStoreState();
  const { slideLeftFullPage } = useViewTransition();

  const giveaway_link_route = useMemo(
    () =>
      `${window.location.origin}${
        router.buildLocation({
          to: giveaway_ad_route.to,
          params: {
            programId: giveaway.programId,
          },
          search: {
            showHeader: false,
            showAccountAndBalance: false,
            giveawayId: giveaway.giveawayId,
          },
        }).href
      }`,
    [giveaway_ad_route.to.toString(), giveaway.programId, giveaway.giveawayId]
  );

  const deeplink_route = getDeeplinkBrowserUrl(
    giveaway_link_route,
    'fullScreenCover'
  );

  if (isPuzzleWalletWebview) {
    return (
      <a
        href={deeplink_route}
        target='_blank'
        className='w-full max-w-[500px] justify-items-center'
      >
        <GiveawayHistoryRowView giveaway={giveaway} />
      </a>
    );
  }

  return (
    <Link
      className='w-full max-w-[500px] justify-items-center'
      to={giveaway_ad_route.to}
      from={index_route.fullPath}
      params={{
        programId: giveaway.programId,
      }}
      search={{
        showHeader: true,
        giveawayId: giveaway.giveawayId,
        showAccountAndBalance: true,
      }}
      viewTransition={slideLeftFullPage}
    >
      <GiveawayHistoryRowView giveaway={giveaway} />
    </Link>
  );
};
